import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextZustand';

function formatDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR');
}

function pluralizeDays(days?: number): string | null {
  if (days == null) return null;
  return days === 1 ? '1 dia restante' : `${days} dias restantes`;
}

/**
 * Banner contextual de assinatura. Mantém o nome público antigo para evitar
 * churn nos imports, mas agora representa todos os estados de acesso.
 */
export const SubscriptionRequiredBanner: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const accessReason = user.accessReason;
  const accessLevel = user.accessLevel;
  const daysText = pluralizeDays(user.daysRemaining);
  const accessUntilText = formatDate(user.accessUntil ?? user.trialEndsAt);

  const banner = (() => {
    switch (accessReason) {
      case 'pending':
        return {
          className: 'bg-amber-600 text-white',
          title: 'Aguardando confirmação do pagamento',
          message: 'Você pode continuar usando o sistema enquanto confirmamos sua assinatura.',
          action: 'Ver assinatura',
        };
      case 'expired_grace':
        return {
          className: 'bg-orange-600 text-white',
          title: 'Regularize sua assinatura',
          message: `Você ainda tem acesso completo${daysText ? ` por ${daysText}` : ''}. Regularize até ${accessUntilText ?? 'o prazo indicado'} para evitar bloqueio.`,
          action: 'Regularizar agora',
        };
      case 'cancelled_until':
        return {
          className: 'bg-slate-700 text-white',
          title: 'Assinatura cancelada',
          message: `Seu plano continua ativo até ${accessUntilText ?? 'o fim do ciclo atual'}. Você pode renovar antes desse prazo.`,
          action: 'Renovar assinatura',
        };
      case 'trial':
        return {
          className: 'antialiased',
          style: {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
          },
          title: 'Período de avaliação ativo',
          message: `Conheça os planos antes do fim do trial${daysText ? `: ${daysText}` : ''}.`,
          action: 'Ver planos',
        };
      case 'trial_expired':
        return {
          className: 'antialiased',
          style: {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
          },
          title: 'Seu período de avaliação terminou',
          message: 'Escolha um plano para liberar novamente o acesso completo.',
          action: 'Começar assinatura',
        };
      case 'inactive':
        return {
          className: 'antialiased',
          style: {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
          },
          title: 'Acesso restrito',
          message: 'Escolha um plano ou regularize o pagamento para voltar a usar o sistema.',
          action: 'Reativar acesso',
        };
      default:
        if (user.needsSubscription || accessLevel === 'RESTRICTED') {
          return {
            className: 'antialiased',
            style: {
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-on-primary)',
            },
            title: 'Acesso restrito',
            message: 'Complete sua assinatura para ter acesso total ao sistema.',
            action: 'Ver planos',
          };
        }
        return null;
    }
  })();

  if (!banner) return null;

  return (
    <div className={banner.className} style={'style' in banner ? banner.style : undefined}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm sm:text-base">{banner.title}</p>
              <p className="text-xs sm:text-sm opacity-90 mt-1">{banner.message}</p>
            </div>
          </div>
          <Link to="/subscription" className="text-sm font-semibold underline hover:no-underline">
            {banner.action}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const SubscriptionStatusBanner = SubscriptionRequiredBanner;

/**
 * Componente compacto usado em bloqueios de rota e estados vazios.
 */
export const SubscriptionRequired: React.FC<{
  title?: string;
  message?: string;
}> = ({
  title = 'Assinatura Necessária',
  message = 'Você precisa ter uma assinatura ativa para acessar esta funcionalidade.',
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-orange-100 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-600" />
        </div>

        <h1 className="text-3xl font-bold admin-text-title mb-4">{title}</h1>
        <p className="admin-text-body mb-8">{message}</p>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            Ver Planos de Assinatura
          </button>

          <button
            onClick={() => navigate('/settings')}
            className="w-full px-6 py-3 bg-gray-200 admin-text-body rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Ir para Configurações
          </button>
        </div>
      </div>
    </div>
  );
};
